import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import TreatmentDetailModal from "@/components/TreatmentDetailModal";
import ProductInfoModal from "@/components/ProductInfoModal";
import ConsultationChooserModal from "@/components/ConsultationChooserModal";
import { api, gbp } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { allergyWarningFor } from "@/lib/treatmentWarnings";
import { readCache, writeTreatments, writePromotions } from "@/lib/catalogueCache";
import "@/styles/promotions.css";

export default function Catalogue() {
  // Stale-while-revalidate: seed from the module-scoped cache so the
  // grid paints instantly on repeat visits, then refetch in the
  // background. First-time visitors still see the fetch loading
  // window, but every subsequent navigation / tab return is instant.
  const _cached = readCache();
  const [treatments, setTreatments] = useState(_cached.treatments || []);
  const [promoMap, setPromoMap] = useState(_cached.promoMap || {});     // treatment_id → active promo
  const [promoList, setPromoList] = useState(_cached.promoList || []);  // active promos (for banner)
  const [searchParams] = useSearchParams();
  const [activeCat, setActiveCat] = useState(searchParams.get("category") || "All");
  // const [detail, setDetail] = useState(null);
    const [detail, setDetail] = useState(null);
  const [detailHidePrice, setDetailHidePrice] = useState(false);
  // Opens the detail popup. hidePrice=true for cards that don't sell
  // themselves directly (variant-group headers, Targeted Skin Booster) —
  // purchasing happens via the option buttons on the card instead.
  const openDetail = (t, hidePrice = false) => {
    setDetail(t);
    setDetailHidePrice(hidePrice);
  };

  const [consultationPick, setConsultationPick] = useState(null);
  // Product-information popup — shown BEFORE the client adds a serum /
  // skin booster option to their cart. If the option's treatment record
  // carries a `product_info` block the modal opens with that content;
  // otherwise the item is added straight away (preserves the existing
  // behaviour for products that haven't been documented yet).
  const [productInfoPick, setProductInfoPick] = useState(null);
  const { addToCart } = useApp();

  // If the URL changes (e.g. Header CTA taps ?category=Consultation while
  // already on this page), sync the filter to the query param.
  useEffect(() => {
    const q = searchParams.get("category");
    if (q) setActiveCat(q);
  }, [searchParams]);

  const isConsultation = (t) => (t?.category || "").toLowerCase() === "consultation"
    || (t?.name || "").toLowerCase() === "consultation";

  const handleAdd = (t) => {
    if (isConsultation(t)) { setConsultationPick(t); return; }
    // If this product has a documented product_info block, show the
    // information modal first so the client can review ingredients +
    // precautions before their selection is committed to the cart.
    if (t && t.product_info && Object.keys(t.product_info).length > 0) {
      setProductInfoPick(t);
      return;
    }
    addToCart(t);
    toast.success(`Added ${t.name}`);
  };

  // "Continue to Add to Cart" from the product-information modal — commits
  // the selection then closes the modal. Guarded against duplicate adds
  // by the modal's own `isAdding` state.
  const confirmProductInfo = (t) => {
    addToCart(t);
    toast.success(`Added ${t.name}`);
    setProductInfoPick(null);
  };

  const loadTreatments = () =>
    api.get("/treatments")
      .then((r) => { setTreatments(r.data); writeTreatments(r.data); })
      // Only surface the error toast when we've got nothing to show —
      // if the cache already seeded cards, a transient network blip
      // shouldn't nag the customer.
      .catch(() => { if (!(_cached.treatments || []).length) toast.error("Couldn't load treatments"); });

  const loadPromotions = () =>
    api.get("/promotions/active")
      .then((r) => {
        const by = r.data.by_treatment || {};
        const list = r.data.promotions || [];
        setPromoMap(by);
        setPromoList(list);
        writePromotions(by, list);
      })
      .catch(() => { /* silent — promotions are additive */ });

  useEffect(() => {
    loadTreatments();
    loadPromotions();
    const onVis = () => {
      if (document.visibilityState === "visible") { loadTreatments(); loadPromotions(); }
    };
    const onFocus = () => { loadTreatments(); loadPromotions(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const categories = useMemo(() => {
    const cats = [...new Set(treatments.map((t) => t.category))];
    const isConsult = (c) => (c || "").toLowerCase() === "consultation";
    const consult = cats.filter(isConsult);
    const rest = cats.filter((c) => !isConsult(c));
    return ["All", ...consult, ...rest];
  }, [treatments]);

  // const filtered = useMemo(() => {
  //   return treatments.filter((t) => activeCat === "All" || t.category === activeCat);
  // }, [treatments, activeCat]);

  // const grouped = useMemo(() => {
  //   const groups = {};
  //   for (const t of filtered) (groups[t.category] ||= []).push(t);
  //   return groups;
  // }, [filtered]);
  const filtered = useMemo(() => {
    return treatments.filter((t) => activeCat === "All" || t.category === activeCat);
  }, [treatments, activeCat]);

  // Manual option nesting — treatments that should render as selectable
  // options inside another treatment's own card, by exact treatment name.
  // Pure presentation: doesn't touch the underlying treatment records,
  // slugs, prices, consent or aftercare wiring.
    const MANUAL_OPTION_GROUPS = {
    "Targeted Skin Booster ": ["NAD+POLY Acne Repair", "iLLUMA Crystal Rose"],
    // Microneedling add-on grouping — parent cards keep their own
    // description / preparation / aftercare modal (hidePriceAndAdd), and
    // each option button adds a real, pre-seeded Microneedling + add-on
    // child treatment to the cart with the right price and the shared
    // Microneedling consent form attached. Names below MUST match the
    // DB treatment names exactly; a mismatch silently renders nothing.
    "Microneedling – 1 Area": [
      "Microneedling – 1 Area + NAD+ PN Acne Repair",
      "Microneedling – 1 Area + iLLUMA Crystal Rose+ Brighten & Radiance",
      "Microneedling – 1 Area + Lumi-Meso",
    ],
    "Microneedling – 2 Areas": [
      "Microneedling – 2 Areas + NAD+ PN Acne Repair",
      "Microneedling – 2 Areas + iLLUMA Crystal Rose+ Brighten & Radiance",
      "Microneedling – 2 Areas + Lumi-Meso",
    ],
    "Microneedling - Course of 3": [
      "Microneedling - Course of 3 + NAD+ PN Acne Repair",
      "Microneedling - Course of 3 + iLLUMA Crystal Rose+ Brighten & Radiance",
      "Microneedling - Course of 3 + Lumi-Meso",
    ],
    "Microneedling - Course of 6": [
      "Microneedling - Course of 6 + NAD+ PN Acne Repair",
      "Microneedling - Course of 6 + iLLUMA Crystal Rose+ Brighten & Radiance",
      "Microneedling - Course of 6 + Lumi-Meso",
    ],
    "Microneedling + Skin Booster": [
      "Microneedling + Skin Booster + NAD+ PN Acne Repair",
      "Microneedling + Skin Booster + iLLUMA Crystal Rose",
    ],
  };


  const grouped = useMemo(() => {
    const childNames = new Set(Object.values(MANUAL_OPTION_GROUPS).flat());
    const byName = {};
    for (const t of filtered) byName[t.name] = t;

    const groups = {};
    for (const t of filtered) {
      if (childNames.has(t.name)) continue; // rendered nested under its parent instead
      (groups[t.category] ||= []).push(t);
    }

    for (const [parentName, optionNames] of Object.entries(MANUAL_OPTION_GROUPS)) {
      const parent = byName[parentName];
      if (!parent) continue;
      const cat = groups[parent.category];
      if (!cat) continue;
      const idx = cat.findIndex((t) => t.name === parentName);
      if (idx === -1) continue;
      const options = optionNames.map((n) => byName[n]).filter(Boolean);
      // Short option-button label — if the child name begins with the
      // parent name + " + " / " · " / " – ", strip that prefix so the
      // button reads e.g. "NAD+ PN Acne Repair" instead of the full
      // "Microneedling – 1 Area + NAD+ PN Acne Repair". Falls back to
      // the full child name when there's no shared prefix.
      const strippedOptions = options.map((o) => {
        const n = o.name || "";
        for (const sep of [" + ", " · ", " – ", " - "]) {
          const prefix = parentName + sep;
          if (n.startsWith(prefix)) return { ...o, _optionLabel: n.slice(prefix.length).trim() };
        }
        return o;
      });
      if (strippedOptions.length) cat[idx] = { ...parent, _manualOptions: strippedOptions };
    }
    return groups;
  }, [filtered]);

  // Group treatment variants that share a base name — e.g. "Lip Filler – 0.5ml"
  // and "Lip Filler – 1ml" collapse to a single "Lip Filler" row with two
  // option cards. Pure presentation — the underlying treatment records, IDs,
  // prices, consent-form keys, aftercare templates etc. are untouched. If a
  // treatment name doesn't match the variant pattern, it stays on its own row.
  // Course treatments → the standard single-treatment slug they bundle
  // (mirrors backend COURSE_CONFIG.converts_to_slug). Used to auto-calculate
  // the "SAVE £X" badge shown next to course prices on the menu — single
  // treatment price × number of sessions minus course price.
  const COURSE_SAVINGS = {
    "microneedling-course-of-3":   { singleSlug: "microneedling-face",  sessions: 3 },
    "microneedling-course-of-6":   { singleSlug: "microneedling-face",  sessions: 6 },
    "profhilo-course-of-2":        { singleSlug: "profhilo-1-session",  sessions: 3 },
    "polynucleotides-course-of-3": { singleSlug: "polynucleotides-face", sessions: 3 },
  };
  const bySlug = useMemo(() => {
    const map = {};
    for (const t of treatments) map[t.slug] = t;
    return map;
  }, [treatments]);
  const courseSavingsFor = (t) => {
    const cfg = COURSE_SAVINGS[t?.slug];
    if (!cfg) return 0;
    const single = bySlug[cfg.singleSlug];
    if (!single) return 0;
    const saved = Number(single.price) * cfg.sessions - Number(t.price);
    return saved > 0 ? Math.round(saved) : 0;
  };

  const extractBase = (name) =>
    (name || "").replace(/\s*[–-]\s*\d+(?:\.\d+)?\s*ml.*$/i, "").trim();
  const extractVariantLabel = (name) => {
    const m = (name || "").match(/(\d+(?:\.\d+)?)\s*ml/i);
    return m ? `${m[1]} ML` : name;
  };
  const buildVariantGroups = (items) => {
    const order = [];
    const map = new Map();
    for (const t of items) {
      const base = extractBase(t.name) || t.name;
      if (!map.has(base)) { map.set(base, []); order.push(base); }
      map.get(base).push(t);
    }
    return order.map((base) => ({ base, items: map.get(base) }));
  };

  return (
    <>
      <Header />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 pt-14 pb-24">
        {/* Active-promotion banner — subtle, hides itself when no
            promotion is live. Uses the first active promotion's theme
            colours so it feels like a seasonal edit rather than a
            generic discount stripe. */}
        {promoList.length > 0 && <PromotionBanner promo={promoList[0]} />}
        <section className="fade-in">
          <p className="mono-label mb-4">CLAÈ · Aesthetic Menu</p>
          <h1 className="font-serif text-[1.9rem] sm:text-[2.55rem] lg:text-[3.2rem] font-light text-stone-900 leading-none">Treatments</h1>
          <p className="mt-6 max-w-xl text-stone-600 leading-relaxed">
            A considered menu of injectable, skin and collagen treatments. Choose your treatment,
            select a time that suits, and secure your appointment with a small deposit.
          </p>
        </section>

        <section className="mt-14 flex justify-start">
          <div className="flex gap-2 flex-wrap">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setActiveCat(c)}
                className={`mono-label px-3 py-2 rounded-sm border transition ${
                  activeCat === c ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-600 hover:border-stone-900"
                }`}
                style={activeCat === c ? { color: "#fff" } : {}}
              >
                {c}
              </button>
            ))}
          </div>
        </section>

        <section className="mt-14 space-y-16">
          {Object.keys(grouped).length === 0 && (
            <p className="text-stone-500 py-24 text-center">No treatments match your search.</p>
          )}
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} data-testid={`group-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="flex items-baseline justify-between mb-6 pb-4 border-b hairline">
                <h2 className="font-serif text-[1.78rem] text-stone-900 font-light">{cat}</h2>
                <span className="mono-label">{items.length} treatments</span>
              </div>
              <ul>
                               {buildVariantGroups(items).map((grp) => {
                  if (grp.items.length < 2) {
                    const t = grp.items[0];
                                        if (t._manualOptions) {
                      return (
                        <ParentWithOptionsRow
                          key={t.id}
                          t={t}
                          options={t._manualOptions}
                          promo={promoMap[t.id]}
                          promoMap={promoMap}
                          isConsultation={isConsultation}
                          onOpen={() => openDetail(t, true)}
                          onAdd={handleAdd}
                        />
                      );
                    }
                    return (
                      <SingleTreatmentRow
                        key={t.id}
                        t={t}
                        promo={promoMap[t.id]}
                        isConsultation={isConsultation}
                        courseSavings={courseSavingsFor(t)}
                        onOpen={() => openDetail(t)}
                        onAdd={handleAdd}
                      />
                    );
                  }

                  return (
                    <VariantGroupRow
                      key={grp.base}
                      base={grp.base}
                      items={grp.items}
                      promoMap={promoMap}
                      onAdd={handleAdd}
                    onOpen={(t) => openDetail({ ...t, name: extractBase(t.name) || t.name }, true)}
                      extractVariantLabel={extractVariantLabel}
                    />
                  );

                })}
              </ul>
            </div>
          ))}
        </section>
      </main>
      {/* {detail && (
        <TreatmentDetailModal treatment={detail} onClose={() => setDetail(null)} onAdd={(t) => { setDetail(null); handleAdd(t); }} />
      )} */}
            {detail && (
        <TreatmentDetailModal
          treatment={detail}
          hidePriceAndAdd={detailHidePrice}
          onClose={() => { setDetail(null); setDetailHidePrice(false); }}
          onAdd={(t) => { setDetail(null); setDetailHidePrice(false); handleAdd(t); }}
        />
      )}

      {consultationPick && (
        <ConsultationChooserModal
          treatment={consultationPick}
          onClose={() => setConsultationPick(null)}
          onConfirm={(extras) => {
            addToCart(consultationPick, extras);
            toast.success(`Added Consultation · ${extras.consultation_target_label}`);
            setConsultationPick(null);
          }}
        />
      )}

      {productInfoPick && (
        <ProductInfoModal
          treatment={productInfoPick}
          onClose={() => setProductInfoPick(null)}
          onConfirm={confirmProductInfo}
        />
      )}
    </>
  );
}

/* Subtle seasonal promotion banner. Uses the promo's own theme colours
   via CSS variables so different campaigns feel distinct without
   requiring separate templates. Hides itself when no promotion is
   active — parent gates on `promoList.length`. */
function PromotionBanner({ promo }) {
  const style = {
    "--promo-primary": promo.colors?.primary || "#141414",
    "--promo-accent":  promo.colors?.accent  || "#C5A059",
    "--promo-text":    promo.colors?.text    || "#FAF9F6",
    background: "var(--promo-primary)",
    color: "var(--promo-text)",
  };
  const themeLabel = { christmas: "Christmas at CLAÈ", valentines: "Valentine's at CLAÈ",
    summer: "Summer at CLAÈ", black_friday: "Black Friday at CLAÈ",
    new_year: "New Year at CLAÈ", custom: promo.name }[promo.theme] || promo.name;
  return (
    <div className="mb-12 px-5 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 hairline"
         style={style} data-testid="promotion-banner">
      <div>
        <p className="mono-label text-[10px]" style={{ color: "var(--promo-accent)" }}>{themeLabel.toUpperCase()}</p>
        <p className="font-serif text-lg sm:text-xl mt-0.5">
          Enjoy {promo.discount_pct}% off {promo.description || "selected treatments"}.
        </p>
      </div>
    </div>
  );
}

/* ─── Treatment row renderers ──────────────────────────────────────── */
/* Default single-treatment row — identical to the pre-grouping layout.
   Factored out so VariantGroupRow can live alongside without duplicating
   promo styling, add-to-cart wiring or the hover affordance. */
function SingleTreatmentRow({ t, promo, isConsultation, courseSavings = 0, onOpen, onAdd }) {
  const promoPrice = promo
    ? Math.round(Number(t.price) * (1 - Number(promo.discount_pct) / 100) * 100) / 100
    : null;
  const seasonal = { christmas: "sparkle", valentines: "glow", summer: "shimmer",
    black_friday: "pulse", new_year: "shimmer", custom: "shimmer" };
  const animName = promo
    ? (promo.animation === "seasonal" ? seasonal[promo.theme] || "shimmer" : promo.animation)
    : null;
  const promoStyle = promo ? {
    "--promo-primary": promo.colors?.primary || "#141414",
    "--promo-accent":  promo.colors?.accent  || "#C5A059",
    "--promo-button":  promo.colors?.button  || "#141414",
    "--promo-text":    promo.colors?.text    || "#FAF9F6",
  } : {};
  return (
    <li
      className="row-hover group flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8 py-5 border-b hairline cursor-pointer"
      onClick={onOpen}
      data-testid={`treatment-row-${t.slug}`}
      style={promoStyle}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="font-serif text-lg sm:text-xl text-stone-900 leading-snug">{t.name}</h3>
          {promo && (
            <span className="promo-badge mono-label text-[10px]" data-testid={`promo-badge-${t.slug}`}>
              {promo.badge_label}
            </span>
          )}
        </div>
        {allergyWarningFor(t) && (
          <p className="mt-1 text-[12px] italic text-stone-500" data-testid={`allergy-warning-${t.slug}`}>
            {allergyWarningFor(t)}
          </p>
        )}
        <p className="mt-1.5 text-sm text-stone-600 max-w-2xl">{t.short_description}</p>
        <p className="mt-1.5 mono-label">{t.duration_min} min{isConsultation(t) ? "" : ` · Deposit £${Number(t.deposit_value).toFixed(0)}`}</p>
      </div>
      <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-6 w-full sm:w-auto">
        {promo ? (
          <div className="flex items-baseline gap-2" data-testid={`promo-price-${t.slug}`}>
            <span className="text-stone-400 line-through text-sm">{gbp(t.price)}</span>
            <span className="font-serif text-xl sm:text-2xl text-stone-900">{gbp(promoPrice)}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2.5">
            <span className="font-serif text-xl sm:text-2xl text-stone-900" data-testid={`price-${t.slug}`}>{gbp(t.price)}</span>
            {courseSavings > 0 && (
              <span
                data-testid={`course-savings-${t.slug}`}
                className="mono-label text-[9px] tracking-[0.22em] px-2 py-1 border border-stone-900 text-stone-900 bg-stone-50 whitespace-nowrap"
              >
                SAVE £{courseSavings}
              </span>
            )}
          </div>
        )}
        <button
          data-testid={`add-to-cart-${t.slug}`}
          onClick={(e) => { e.stopPropagation(); onAdd(t); }}
          className={promo
            ? `promo-cta whitespace-nowrap px-4 py-2 mono-label border animate-${animName}`
            : "whitespace-nowrap border border-stone-900 px-4 py-2 mono-label text-stone-900 hover:bg-stone-900 hover:text-white transition"}
        >
          Add to Cart
        </button>
      </div>
    </li>
  );
}
/* Parent treatment that also surfaces hand-picked sibling treatments as
   clickable option buttons inside its own card (e.g. Targeted Skin Booster
   surfacing NAD+POLY Acne Repair and iLLUMA Crystal Rose). Unlike
   VariantGroupRow, the parent itself stays a real, independently clickable
   treatment — openable / addable on its own — the options are additional
   real treatments nested here purely for discoverability. */

function ParentWithOptionsRow({ t, options, promo, promoMap, isConsultation, onOpen, onAdd }) {
  return (
    <li
      className="row-hover group flex flex-col gap-4 py-5 border-b hairline cursor-pointer"
      onClick={onOpen}
      data-testid={`treatment-row-${t.slug}`}
    >
      <div className="flex items-baseline gap-3 flex-wrap">
        <h3 className="font-serif text-lg sm:text-xl text-stone-900 leading-snug">{t.name}</h3>
        {promo && (
          <span className="promo-badge mono-label text-[10px]" data-testid={`promo-badge-${t.slug}`}>
            {promo.badge_label}
          </span>
        )}
      </div>
      {allergyWarningFor(t) && (
        <p className="mt-1 text-[12px] italic text-stone-500" data-testid={`allergy-warning-${t.slug}`}>
          {allergyWarningFor(t)}
        </p>
      )}
      <p className="mt-1.5 text-sm text-stone-600 max-w-2xl">{t.short_description}</p>
      <p className="mt-1.5 mono-label">{t.duration_min} min{isConsultation(t) ? "" : ` · Deposit £${Number(t.deposit_value).toFixed(0)}`}</p>

      <div className="mt-4 grid grid-cols-2 sm:flex sm:flex-wrap gap-3">
        {options.map((opt) => {
          const optPromo = promoMap[opt.id];
          const optPromoPrice = optPromo
            ? Math.round(Number(opt.price) * (1 - Number(optPromo.discount_pct) / 100) * 100) / 100
            : null;
          const hasInfo = opt.product_info && Object.keys(opt.product_info).length > 0;
          return (
            <button
              key={opt.id}
              type="button"
              data-testid={`add-to-cart-${opt.slug}`}
              onClick={(e) => { e.stopPropagation(); onAdd(opt); }}
              className="group/opt text-left border border-stone-300 hover:border-stone-900
                         transition-colors px-4 py-3 sm:min-w-[180px]
                         focus:outline-none focus:ring-2 focus:ring-stone-900/20
                         flex flex-col gap-1"
              aria-label={hasInfo ? `View product information for ${opt.name}` : `Add ${opt.name} to cart`}
            >
              <span className="mono-label text-[11px] tracking-[0.22em] text-stone-500 group-hover/opt:text-stone-900">
                {opt._optionLabel || opt.name}
              </span>
              {optPromo ? (
                <span className="flex items-baseline gap-2" data-testid={`promo-price-${opt.slug}`}>
                  <span className="text-stone-400 line-through text-[11px]">{gbp(opt.price)}</span>
                  <span className="font-serif text-lg sm:text-xl text-stone-900">{gbp(optPromoPrice)}</span>
                </span>
              ) : (
                <span className="font-serif text-lg sm:text-xl text-stone-900" data-testid={`price-${opt.slug}`}>
                  {gbp(opt.price)}
                </span>
              )}
              <span
                className="mono-label text-[10px] text-stone-500 group-hover/opt:text-stone-900"
                data-testid={`option-cta-${opt.slug}`}
              >
                {hasInfo ? "View Product Information →" : "Add to Cart →"}
              </span>
            </button>
          );
        })}
      </div>
    </li>
  );
}

// function ParentWithOptionsRow({ t, options, promo, promoMap, isConsultation, onOpen, onAdd }) {
//   const promoPrice = promo
//     ? Math.round(Number(t.price) * (1 - Number(promo.discount_pct) / 100) * 100) / 100
//     : null;
//   return (
//     <li
//       className="row-hover group flex flex-col gap-4 py-5 border-b hairline cursor-pointer"
//       onClick={onOpen}
//       data-testid={`treatment-row-${t.slug}`}
//     >
//       <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8">
//         <div className="flex-1 min-w-0">
//           <div className="flex items-center gap-3 flex-wrap">
//             <h3 className="font-serif text-lg sm:text-xl text-stone-900 leading-snug">{t.name}</h3>
//             {promo && (
//               <span className="promo-badge mono-label text-[10px]" data-testid={`promo-badge-${t.slug}`}>
//                 {promo.badge_label}
//               </span>
//             )}
//           </div>
//           {allergyWarningFor(t) && (
//             <p className="mt-1 text-[12px] italic text-stone-500" data-testid={`allergy-warning-${t.slug}`}>
//               {allergyWarningFor(t)}
//             </p>
//           )}
//           <p className="mt-1.5 text-sm text-stone-600 max-w-2xl">{t.short_description}</p>
//           <p className="mt-1.5 mono-label">{t.duration_min} min{isConsultation(t) ? "" : ` · Deposit £${Number(t.deposit_value).toFixed(0)}`}</p>
//         </div>
//         <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-6 w-full sm:w-auto">
//           {promo ? (
//             <div className="flex items-baseline gap-2" data-testid={`promo-price-${t.slug}`}>
//               <span className="text-stone-400 line-through text-sm">{gbp(t.price)}</span>
//               <span className="font-serif text-xl sm:text-2xl text-stone-900">{gbp(promoPrice)}</span>
//             </div>
//           ) : (
//             <span className="font-serif text-xl sm:text-2xl text-stone-900" data-testid={`price-${t.slug}`}>{gbp(t.price)}</span>
//           )}
//           <button
//             data-testid={`add-to-cart-${t.slug}`}
//             onClick={(e) => { e.stopPropagation(); onAdd(t); }}
//             className="whitespace-nowrap border border-stone-900 px-4 py-2 mono-label text-stone-900 hover:bg-stone-900 hover:text-white transition"
//           >
//             Add to Cart
//           </button>
//         </div>
//       </div>

//       <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3">
//         {options.map((opt) => {
//           const optPromo = promoMap[opt.id];
//           const optPromoPrice = optPromo
//             ? Math.round(Number(opt.price) * (1 - Number(optPromo.discount_pct) / 100) * 100) / 100
//             : null;
//           return (
//             <button
//               key={opt.id}
//               type="button"
//               data-testid={`add-to-cart-${opt.slug}`}
//               onClick={(e) => { e.stopPropagation(); onAdd(opt); }}
//               className="group/opt text-left border border-stone-300 hover:border-stone-900
//                          transition-colors px-4 py-3 sm:min-w-[180px]
//                          focus:outline-none focus:ring-2 focus:ring-stone-900/20
//                          flex flex-col gap-1"
//               aria-label={`Add ${opt.name} to cart`}
//             >
//               <span className="mono-label text-[11px] tracking-[0.22em] text-stone-500 group-hover/opt:text-stone-900">
//                 {opt.name}
//               </span>
//               {optPromo ? (
//                 <span className="flex items-baseline gap-2" data-testid={`promo-price-${opt.slug}`}>
//                   <span className="text-stone-400 line-through text-[11px]">{gbp(opt.price)}</span>
//                   <span className="font-serif text-lg sm:text-xl text-stone-900">{gbp(optPromoPrice)}</span>
//                 </span>
//               ) : (
//                 <span className="font-serif text-lg sm:text-xl text-stone-900" data-testid={`price-${opt.slug}`}>
//                   {gbp(opt.price)}
//                 </span>
//               )}
//               <span className="mono-label text-[10px] text-stone-500 group-hover/opt:text-stone-900">
//                 Add to Cart →
//               </span>
//             </button>
//           );
//         })}
//       </div>
//     </li>
//   );
// }


/* Variant group row — one heading (e.g. "Lip Filler") with a selectable
   card per underlying treatment variant underneath. Each option card maps
   1:1 to a real treatment record, so clicking Add on 0.5 ML passes the
   actual "Lip Filler – 0.5ml" treatment into the cart with its own id,
   price, duration, consent & aftercare wiring preserved. */
function VariantGroupRow({ base, items, promoMap, onAdd, onOpen, extractVariantLabel }) {
  const sorted = [...items].sort(
    (a, b) => (a.order ?? a.position ?? 0) - (b.order ?? b.position ?? 0),
  );
  const first = sorted[0];
  const baseSlug = base.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    // <li
    //   className="py-5 border-b hairline cursor-pointer"
    //   onClick={() => first && onOpen && onOpen(first)}
    //   data-testid={`treatment-group-${baseSlug}`}
    // >
    <li
      className="row-hover group py-5 border-b hairline cursor-pointer"
      onClick={() => first && onOpen && onOpen(first)}
      data-testid={`treatment-group-${baseSlug}`}
    >

      <div className="flex items-baseline gap-3 flex-wrap">
        <h3 className="font-serif text-lg sm:text-xl text-stone-900 leading-snug">{base}</h3>
      </div>
      {first && allergyWarningFor(first) && (
        <p className="mt-1 text-[12px] italic text-stone-500" data-testid={`allergy-warning-${baseSlug}`}>
          {allergyWarningFor(first)}
        </p>
      )}
      {first?.short_description && (
        <p className="mt-1.5 text-sm text-stone-600 max-w-2xl">{first.short_description}</p>
      )}
      <p className="mt-1.5 mono-label">
        {first?.duration_min} min · Deposit £{Number(first?.deposit_value || 0).toFixed(0)}
      </p>
      <div className="mt-4 grid grid-cols-2 sm:flex sm:flex-wrap gap-3">
        {sorted.map((t) => {
          const promo = promoMap[t.id];
          const promoPrice = promo
            ? Math.round(Number(t.price) * (1 - Number(promo.discount_pct) / 100) * 100) / 100
            : null;
          const label = extractVariantLabel(t.name);
          return (
            <button
              key={t.id}
              type="button"
              data-testid={`add-to-cart-${t.slug}`}
              onClick={(e) => { e.stopPropagation(); onAdd(t); }}
              className="group/opt text-left border border-stone-300 hover:border-stone-900
                         transition-colors px-4 py-3 sm:min-w-[160px]
                         focus:outline-none focus:ring-2 focus:ring-stone-900/20
                         flex flex-col gap-1"
              aria-label={`Add ${t.name} to cart`}
            >
              <span className="mono-label text-[11px] tracking-[0.22em] text-stone-500 group-hover/opt:text-stone-900">
                {label}
              </span>
              {promo ? (
                <span className="flex items-baseline gap-2" data-testid={`promo-price-${t.slug}`}>
                  <span className="text-stone-400 line-through text-[11px]">{gbp(t.price)}</span>
                  <span className="font-serif text-lg sm:text-xl text-stone-900">{gbp(promoPrice)}</span>
                </span>
              ) : (
                <span className="font-serif text-lg sm:text-xl text-stone-900" data-testid={`price-${t.slug}`}>
                  {gbp(t.price)}
                </span>
              )}
              <span className="mono-label text-[10px] text-stone-500 group-hover/opt:text-stone-900">
                Add to Cart →
              </span>
            </button>
          );
        })}
      </div>
    </li>
  );
}
